import React, { FC, useMemo } from 'react';

import PluginEntitiesProvider from './PluginEntitiesProvider';
import { useRelationshipsContext } from './RelationshipsProvider';
import getPluginKey from '../helpers/getPluginKey';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';

interface LinkedEntitiesProviderProps {
  children: React.ReactNode;
  sourceEntityDomain: string;
  sourceEntityType: string;
  plugins: CommonReferencePickerPlugin[];
}

interface EntityIdsByDomainAndType {
  [domain: string]: {
    [type: string]: string[];
  };
}

const LinkedEntitiesProvider: FC<LinkedEntitiesProviderProps> = ({
  children,
  sourceEntityDomain,
  sourceEntityType,
  plugins,
}) => {
  const { relationshipDataBySource } = useRelationshipsContext();
  const idsByDomainAndType = useMemo(() => {
    const result: EntityIdsByDomainAndType = {};
    const relationshipsBySourceId =
      relationshipDataBySource[sourceEntityDomain]?.[sourceEntityType] || {};
    const sourceEntityIds = Object.keys(relationshipsBySourceId);
    sourceEntityIds.forEach((sourceEntityId) => {
      const data =
        relationshipDataBySource[sourceEntityDomain]?.[sourceEntityType]?.[
          sourceEntityId
        ];
      if (data?.relationshipsData) {
        data.relationshipsData.forEach(({ domain, type, entityId }) => {
          if (!result[domain]) {
            result[domain] = {};
          }
          if (!result[domain][type]) {
            result[domain][type] = [];
          }
          const domainTypeArr = result[domain][type];
          // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array built within useMemo
          domainTypeArr.push(entityId);
        });
      }
    });
    return result;
  }, [relationshipDataBySource, sourceEntityDomain, sourceEntityType]);

  return plugins.filter(Boolean).reduce((content, plugin) => {
    return (
      <PluginEntitiesProvider
        plugin={plugin}
        key={getPluginKey(plugin)}
        entityIds={
          idsByDomainAndType?.[plugin.metadata.domain]?.[plugin.metadata.type]
        }
      >
        {content}
      </PluginEntitiesProvider>
    );
  }, children);
};

LinkedEntitiesProvider.displayName = 'LinkedEntitiesProvider';

export default LinkedEntitiesProvider;
